import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { tomorrowYmd } from './recurrenceSuggestions';

const FREQUENCY_OPTIONS = [
  { value: '', label: 'Select frequency...' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

interface BulkRecurrenceModalProps {
  open: boolean;
  jobCount: number;
  saving: boolean;
  onClose: () => void;
  onSave: (values: {
    isRecurring: boolean;
    recurrenceFrequency: string;
    recurrenceNextDate: string;
  }) => void;
}

export function BulkRecurrenceModal({
  open,
  jobCount,
  saving,
  onClose,
  onSave,
}: BulkRecurrenceModalProps) {
  const [isRecurring, setIsRecurring] = useState(true);
  const [frequency, setFrequency] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setIsRecurring(true);
    setFrequency('');
    setNextDate('');
    setError('');
  }, [open]);

  function handleSave() {
    if (isRecurring) {
      if (!frequency || !nextDate) {
        setError('Frequency and Next Date are required.');
        return;
      }
      if (nextDate < tomorrowYmd()) {
        setError('Next Date must be tomorrow or later.');
        return;
      }
    }
    setError('');
    onSave({ isRecurring, recurrenceFrequency: frequency, recurrenceNextDate: nextDate });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Update recurrence"
      description={`Apply the same recurrence settings to ${jobCount} selected ${jobCount === 1 ? 'job' : 'jobs'}.`}
      size="lg"
    >
      <div className="mt-4 space-y-4 px-2 sm:px-4">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={isRecurring}
            onChange={(e) => setIsRecurring(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          <span>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Recurring Job
            </span>
            <span className="block text-xs text-gray-500">
              Create a new job automatically on the next date.
            </span>
          </span>
        </label>
        {isRecurring && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Frequency"
              required
              options={FREQUENCY_OPTIONS}
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
            />
            <Input
              label="Next Date"
              type="date"
              required
              min={tomorrowYmd()}
              value={nextDate}
              onChange={(e) => setNextDate(e.target.value)}
            />
          </div>
        )}
        {error && <p className="text-sm text-danger-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Update {jobCount} {jobCount === 1 ? 'job' : 'jobs'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
